import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Building2, Save, Plus, Trash2, Mail, Phone, MapPin, CreditCard } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyProfile = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    company_name: '',
    vat_number: '',
    legal_address: '',
    street: '',
    city: '',
    postal_code: '',
    province: '',
    email: '',
    pec: '',
    iban_list: [],
    phone: '',
    roc_number: ''
  });
  const [newIban, setNewIban] = useState('');

  useEffect(() => {
    if (user?.role === 'superadmin') {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/company-profile`);
      setProfile({
        company_name: response.data.company_name || '',
        vat_number: response.data.vat_number || '',
        legal_address: response.data.legal_address || '',
        street: response.data.street || '',
        city: response.data.city || '',
        postal_code: response.data.postal_code || '',
        province: response.data.province || '',
        email: response.data.email || '',
        pec: response.data.pec || '',
        iban_list: response.data.iban_list || [],
        phone: response.data.phone || '',
        roc_number: response.data.roc_number || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/company-profile`, profile);
      toast.success('Profilo ditta salvato con successo');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleAddIban = () => {
    if (!newIban.trim()) return;
    // Basic IBAN validation (IT format)
    const cleanIban = newIban.replace(/\s/g, '').toUpperCase();
    if (cleanIban.length < 15 || cleanIban.length > 34) {
      toast.error('IBAN non valido');
      return;
    }
    if (profile.iban_list.includes(cleanIban)) {
      toast.error('IBAN già presente');
      return;
    }
    setProfile(prev => ({
      ...prev,
      iban_list: [...prev.iban_list, cleanIban]
    }));
    setNewIban('');
  };

  const handleRemoveIban = (iban) => {
    setProfile(prev => ({
      ...prev,
      iban_list: prev.iban_list.filter(i => i !== iban)
    }));
  };

  const handleChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Caricamento...</div>
      </div>
    );
  }

  // Only Super Admin can access this page
  if (user?.role !== 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="company-profile-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Building2 className="w-8 h-8 text-blue-600" />
            Profilo Ditta
          </h1>
          <p className="text-slate-600 mt-1">Gestisci i dati della tua azienda</p>
        </div>
        <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
          Solo Super Admin
        </Badge>
      </div>

      {/* Main Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Dati Aziendali
            </CardTitle>
            <CardDescription>Informazioni principali della ditta</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Ditta / Ragione Sociale</Label>
              <Input
                value={profile.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                placeholder="es. JV SERVICE SRLS"
                data-testid="company-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>P.IVA</Label>
                <Input
                  value={profile.vat_number}
                  onChange={(e) => handleChange('vat_number', e.target.value)}
                  placeholder="es. IT12345678901"
                  data-testid="vat-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>N. ROC</Label>
                <Input
                  value={profile.roc_number}
                  onChange={(e) => handleChange('roc_number', e.target.value)}
                  placeholder="es. 41825"
                  data-testid="roc-number-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-slate-600" />
              Contatti
            </CardTitle>
            <CardDescription>Email, PEC e telefono</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@azienda.it"
                data-testid="email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>PEC</Label>
              <Input
                type="email"
                value={profile.pec}
                onChange={(e) => handleChange('pec', e.target.value)}
                placeholder="azienda@pec.it"
                data-testid="pec-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefono</Label>
              <Input
                value={profile.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+39 0123 456789"
                data-testid="phone-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-slate-600" />
              Sede Legale
            </CardTitle>
            <CardDescription>Indirizzo della sede legale</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Sede Legale (completa)</Label>
              <Input
                value={profile.legal_address}
                onChange={(e) => handleChange('legal_address', e.target.value)}
                placeholder="es. Via Roma 123, 00100 Roma (RM)"
                data-testid="legal-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Via / Indirizzo</Label>
              <Input
                value={profile.street}
                onChange={(e) => handleChange('street', e.target.value)}
                placeholder="es. Via Roma 123"
                data-testid="street-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Città</Label>
                <Input
                  value={profile.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  placeholder="Roma"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label>CAP</Label>
                <Input
                  value={profile.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  placeholder="00100"
                  data-testid="postal-code-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Provincia</Label>
                <Input
                  value={profile.province}
                  onChange={(e) => handleChange('province', e.target.value)}
                  placeholder="RM"
                  maxLength={2}
                  data-testid="province-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* IBAN List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-slate-600" />
              Coordinate Bancarie
            </CardTitle>
            <CardDescription>Gestisci gli IBAN aziendali</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add IBAN */}
            <div className="flex gap-2">
              <Input
                value={newIban}
                onChange={(e) => setNewIban(e.target.value)}
                placeholder="IT60X0542811101000000123456"
                className="flex-1 font-mono"
                data-testid="new-iban-input"
              />
              <Button onClick={handleAddIban} variant="outline">
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            {/* IBAN List */}
            <div className="space-y-2">
              {profile.iban_list.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">
                  Nessun IBAN configurato
                </p>
              ) : (
                profile.iban_list.map((iban, index) => (
                  <div 
                    key={index} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    data-testid={`iban-item-${index}`}
                  >
                    <span className="font-mono text-sm">{iban}</span>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleRemoveIban(iban)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-slate-900 hover:bg-slate-800 px-8"
          data-testid="save-profile-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Profilo'}
        </Button>
      </div>
    </div>
  );
};

export default CompanyProfile;
